import { isAllowedUpstreamImageHost } from './allowlist';

/**
 * Client-side companion to the backend `toProxiedImageUrl`
 * (`services/backend-api/src/utils/imageProxy.ts`).
 *
 * WEB — "once and for all" image-CSP fix. Only `/events/discover` rewrites
 * provider image URLs server-side; every other surface (the home-feed
 * recommendation rails, search, related, nearby, event detail, …) ships the
 * RAW `Event.imageUrl` straight from the row. Those raw provider hosts
 * (`img.evbuc.com`, `s1.ticketm.net`, …) are deliberately absent from the web
 * CSP `img-src` (and from `next/image` `remotePatterns`), so rendering them
 * directly either trips a CSP violation (raw `<img>`) or throws a
 * "hostname not configured" error (`next/image`).
 *
 * This helper is the single render-time chokepoint: every leaf that renders an
 * event/artist image runs its URL through `toDisplayImageUrl` right before it
 * hits the `<img>` / `next/image` `src`. It normalises any provider URL to the
 * same-origin proxy (`/api/v1/image-proxy?src=…`), which is covered by
 * `img-src 'self'` and is fetchable by the Next optimizer without a
 * `remotePattern`. Because it depends on no API contract, a surface fed by a
 * not-yet-proxied endpoint still renders correctly.
 *
 * Behaviour:
 * - empty / blank / unparseable → `''` (caller renders its gradient fallback)
 * - `data:` / `blob:` → unchanged (already permitted by the CSP)
 * - relative URL (already same-origin) → unchanged
 * - already-proxied (`…/api/v1/image-proxy?src=…`, absolute or relative) →
 *   collapsed to the RELATIVE proxy path so `next/image` never needs the app
 *   host in `remotePatterns` (the server-side discover rewrite emits an
 *   absolute `${frontendBase}` URL)
 * - own CDN (`cdn.loci.city`) → unchanged (served directly, in `remotePatterns`)
 * - Vercel Blob (`*.public.blob.vercel-storage.com`) → unchanged. Durable
 *   editor/admin media uploads (API-1512) live here and are allow-listed in
 *   the CSP `img-src` directly, so they render without the proxy. Without this
 *   passthrough a freshly uploaded image would fall through to "any other
 *   host" and collapse to `''`, blanking the upload preview.
 * - known provider host (Eventbrite / Ticketmaster) → wrapped as the relative
 *   `/api/v1/image-proxy?src=<encoded original>`
 * - any other host → `''` (would be CSP-blocked anyway; degrade to fallback)
 *
 * Idempotent: passing an already-proxied, relative, or own-CDN URL returns an
 * equivalent value, so it is safe to apply at every render site regardless of
 * which API produced the value (or whether that API already proxied it).
 */

const IMAGE_PROXY_PATH = '/api/v1/image-proxy';

/**
 * Ticketmaster DAM host whose `_SOURCE` full-res variant must be downsized
 * before it reaches `next/image` (PIP-148). Mirrors `TICKETMASTER_IMAGE_HOST`
 * in the backend `imageProxy.ts`; pinned by the unit tests in both packages.
 */
const TICKETMASTER_IMAGE_HOST = 's1.ticketm.net';

/**
 * `_SOURCE` token anchored to the end of a TM DAM filename. The full-res
 * `_SOURCE` original (e.g. 3504×1971) exceeds Vercel's optimizer input limit
 * and `400`s through `/_next/image`; we rewrite it to a sized 16:9 variant.
 */
const TICKETMASTER_SOURCE_SUFFIX_PATTERN = /_SOURCE\.[a-z0-9]+$/i;

/**
 * Sized 16:9 variant TM publishes consistently (2048×1152), accepted by the
 * Vercel optimizer. Mirrors the backend `imageProxy.ts` sized suffix and the
 * data-pipeline ingest cap (`TICKETMASTER_MAX_OPTIMIZER_WIDTH_PX = 2048`).
 */
const TICKETMASTER_SIZED_SUFFIX = '_TABLET_LANDSCAPE_LARGE_16_9.jpg';

/**
 * Defense-in-depth (PIP-148): rewrite an already-persisted Ticketmaster
 * `_SOURCE` URL to the sized 16:9 variant so it optimizes through
 * `next/image`. Strictly scoped to `s1.ticketm.net` filenames ending in
 * `_SOURCE.<ext>`; every other URL is returned unchanged. Companion to the
 * backend `rewriteTicketmasterSourceImage` — both rewrite the raw URL before
 * it is wrapped for the proxy.
 */
function rewriteTicketmasterSourceImage(parsed: URL): URL {
  if (parsed.hostname !== TICKETMASTER_IMAGE_HOST) return parsed;
  if (!TICKETMASTER_SOURCE_SUFFIX_PATTERN.test(parsed.pathname)) return parsed;

  const rewritten = new URL(parsed.toString());
  rewritten.pathname = parsed.pathname.replace(
    TICKETMASTER_SOURCE_SUFFIX_PATTERN,
    TICKETMASTER_SIZED_SUFFIX,
  );
  return rewritten;
}

/** Infrastructure we control — rendered directly, present in `remotePatterns`. */
const OWN_CDN_HOSTS: ReadonlySet<string> = new Set(['cdn.loci.city']);

/**
 * Vercel Blob host suffix — durable editor/admin media uploads (API-1512).
 * Buckets are subdomains (`<id>.public.blob.vercel-storage.com`), so this is a
 * suffix match. Allow-listed directly in the web CSP `img-src`, so blob URLs
 * render without the proxy.
 */
const VERCEL_BLOB_HOST_SUFFIX = '.public.blob.vercel-storage.com';

/**
 * RFC 3986 scheme prefix: `<alpha>[<alnum>+.-]*:`
 * Examples: `http:`, `https:`, `data:`, `blob:`.
 */
const SCHEME_PATTERN = /^[a-zA-Z][a-zA-Z\d+.-]*:/;

/** True when `host` is a Vercel Blob bucket we render directly (in the CSP). */
function isVercelBlobHost(host: string): boolean {
  return host.endsWith(VERCEL_BLOB_HOST_SUFFIX);
}

/** True when `url` has no scheme and is not protocol-relative (`//host/…`). */
function isRelativeUrl(url: string): boolean {
  return !SCHEME_PATTERN.test(url) && !url.startsWith('//');
}

/** Build the relative same-origin proxy URL for a raw provider image. */
function toRelativeProxyUrl(originalUrl: string): string {
  const params = new URLSearchParams({ src: originalUrl });
  return `${IMAGE_PROXY_PATH}?${params.toString()}`;
}

export function toDisplayImageUrl(rawImageUrl: string | null | undefined): string {
  if (!rawImageUrl) return '';
  const url = rawImageUrl.trim();
  if (!url) return '';

  // Relative URLs are already same-origin (covers local assets and a
  // relative server-side proxy rewrite). Nothing to do.
  if (isRelativeUrl(url)) return url;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return '';
  }

  // Inline image data the CSP already permits.
  if (parsed.protocol === 'data:' || parsed.protocol === 'blob:') return url;

  // Only http(s) provider images can be proxied; the proxy route itself
  // rejects non-https `src`, so anything else can never render.
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return '';

  // Already pointed at the same-origin proxy (the discover server-side rewrite
  // emits an absolute `${frontendBase}/api/v1/image-proxy?src=…`). Collapse to
  // the relative path so `next/image` never needs the app host in
  // `remotePatterns`, and so re-applying this helper is a no-op.
  if (parsed.pathname === IMAGE_PROXY_PATH) {
    return `${parsed.pathname}${parsed.search}`;
  }

  const host = parsed.hostname;

  // Own CDN — rendered directly (kept in `next/image` `remotePatterns`).
  if (OWN_CDN_HOSTS.has(host)) return url;

  // Vercel Blob upload — rendered directly (allow-listed in the CSP).
  if (isVercelBlobHost(host)) return url;

  // Known provider host (Eventbrite / Ticketmaster) → same-origin proxy.
  if (isAllowedUpstreamImageHost(host)) {
    // PIP-148: downsize a TM `_SOURCE` URL to a sized variant before wrapping
    // it for the proxy, so the optimizer never sees the over-limit original.
    // No-op for non-TM / non-`_SOURCE` URLs.
    const optimizable = rewriteTicketmasterSourceImage(parsed).toString();
    return toRelativeProxyUrl(optimizable);
  }

  // Unknown host: under the tightened CSP this can never load, so collapse to
  // the gradient fallback instead of emitting a guaranteed violation.
  return '';
}
